import { create } from '@bufbuild/protobuf'
import _ from 'lodash'
import { ReactNode, useCallback, useMemo, useState } from 'react'

import { useGetAlertsWithMarkdown, useListAlerts } from '@/api/alerts.hook'
import { useListCompanyArtifact } from '@/api/artifact.hook'
import { useGetCompany, useGetCompanyDrawerMetadata } from '@/api/company.hook'
import { useFetchCompanyData } from '@/api/export-company.hook'
import { ListAlertsRequestSchema } from '@/gen/inventory/v1/alert_service_pb'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'

import { EventName, EventProperties, useTrackCallback } from '@/lib/analytics/events'

import { ExportCompanyContent } from '@/pages/company-drawer/export-company-content/export-company-content'

import { ButtonProps } from '@/components/lemmala-design-system/button/button'
import { ExportButton } from '@/components/shared/export-button/export-button'
import { getInherentRisk } from '@/components/shared/risk-badge/utils'

type ExportCompanyProps = {
  companyId: Company['id']
  assessmentId?: OngoingAssessment['id']
  trackingEvent?: EventName
  extraTrackingProps?: EventProperties
} & ButtonProps

export const ExportCompany = ({
  companyId,
  assessmentId,
  trackingEvent = 'third-party.export',
  extraTrackingProps,
  ...props
}: ExportCompanyProps) => {
  const { data } = useGetCompany(companyId)
  const getExportContent = useExportCompanyContent(companyId, assessmentId)
  const trackExport = useTrackCallback(trackingEvent)

  return (
    <ExportButton
      data-dd-action-name={trackingEvent}
      onClick={() =>
        trackExport({
          companyName: data?.company?.profile?.name,
          ...extraTrackingProps,
        })
      }
      documentTitle={data?.company?.profile?.name}
      getExportContent={getExportContent}
      {...props}
    />
  )
}

const useExportCompanyContent = (
  companyId: Company['id'],
  assessmentId?: OngoingAssessment['id'],
) => {
  let content: ReactNode | null = null
  const [isEnabled, setIsEnabled] = useState(false)

  const { data: artifacts, isLoading: artifactsLoading } = useListCompanyArtifact(companyId)
  const { data: companyDrawerData, isLoading: companyDrawerLoading } = useGetCompanyDrawerMetadata(
    companyId,
    undefined,
    true,
  )
  const { data: alertsData, isLoading: alertsLoading } = useListAlerts(
    create(ListAlertsRequestSchema, { companyId }),
  )

  const {
    loading: companyLoading,
    data: { controlsData, company, inherentRiskGroups },
  } = useFetchCompanyData({ companyId, assessmentId, enabled: isEnabled })

  const alertIds = useMemo(() => (alertsData?.alerts ?? []).map((alert) => alert.id), [alertsData])

  const { data: allAlerts, isPending: isAllAlertsLoading } = useGetAlertsWithMarkdown({
    ids: alertIds,
  })

  const loading =
    companyLoading ||
    artifactsLoading ||
    companyDrawerLoading ||
    alertsLoading ||
    isAllAlertsLoading

  const controlsByCategory = useMemo(() => {
    return _.groupBy(controlsData, (control) => control.control?.categoryName)
  }, [controlsData])

  if (!loading && company && company.company?.profile && companyDrawerData) {
    const { profile, status, statusLastUpdate, validatedRisk, calculatedRisk, alertsByLevel } =
      company.company
    const risk = getInherentRisk(validatedRisk, calculatedRisk)
    const { businessOwner } = companyDrawerData

    const engagementModeEnabled = (company?.company?.orgCompanyEngagements?.length ?? 0) > 0

    content = (
      <ExportCompanyContent
        companyId={companyId}
        artifacts={artifacts?.artifacts || []}
        profile={profile}
        businessOwner={businessOwner}
        inherentRiskGroups={inherentRiskGroups}
        engagementModeEnabled={engagementModeEnabled}
        status={status}
        statusLastUpdate={statusLastUpdate}
        risk={risk}
        alertsByLevel={alertsByLevel}
        controlsByCategory={controlsByCategory}
        allAlerts={allAlerts}
        artifactsLoading={artifactsLoading}
      />
    )
  }

  return useCallback(() => {
    return {
      exportContent: content,
      isLoaded: !loading && !!content,
      renderContent: () => {
        setIsEnabled(true)
      },
    }
  }, [content, loading, setIsEnabled])
}
